import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, CheckCircle, XCircle, Eye, EyeOff, X } from 'lucide-react';
import { Logo } from '../components/Logo';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const { register, login } = useAuth();
  const navigate = useNavigate();

  // Banner state
  const [banner, setBanner] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    // Fetch banner
    const fetchBanner = async () => {
      try {
        const res = await axios.get(`${API}/banner`);
        if (res.data.enabled) {
          setBanner(res.data);
        }
      } catch (e) {
        // Ignore banner errors
      }
    };
    fetchBanner();
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      showNotification('As senhas não coincidem', 'error');
      return;
    }
    
    if (password.length < 6) {
      showNotification('A senha deve ter pelo menos 6 caracteres', 'error');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(name, email, password);
      await login(email, password);
      showNotification('Cadastro realizado! Você tem 7 dias grátis para testar.');
      navigate('/');
    } catch (error) {
      showNotification(error.response?.data?.detail || 'Erro ao fazer cadastro', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
          notification.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
        }`}>
          {notification.type === 'error' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {notification.message}
        </div>
      )}
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          <div className="mb-8">
            <Logo size="lg" />
          </div>
          
          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold">Criar conta</CardTitle>
              <CardDescription>Preencha seus dados para se cadastrar</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome completo</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Seu nome"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="register-name-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="register-email-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Mínimo 6 caracteres"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="register-password-input"
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar senha</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Repita a senha"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      data-testid="register-confirm-password-input"
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 rounded-full font-medium"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Cadastrar'
                  )}
                </Button>
              </form>
              
              <div className="mt-4 p-3 bg-amber-50 rounded-lg text-center">
                <p className="text-sm text-amber-700">
                  🎉 <strong>7 dias grátis</strong> para testar todas as funcionalidades!
                </p>
              </div>
              
              <div className="mt-6 text-center text-sm text-muted-foreground">
                Já tem uma conta?{' '}
                <Link 
                  to="/login" 
                  className="text-primary font-medium hover:underline"
                  data-testid="login-link"
                >
                  Entrar
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Right Side - Visual */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-orange-400 via-amber-500 to-yellow-500 items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <svg className="w-full h-full" viewBox="0 0 800 600" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="200" cy="150" r="100" stroke="white" strokeWidth="40" fill="none"/>
            <circle cx="600" cy="450" r="150" stroke="white" strokeWidth="40" fill="none"/>
            <circle cx="400" cy="300" r="80" stroke="white" strokeWidth="30" fill="none"/>
          </svg>
        </div>
        <div className="relative z-10 max-w-lg text-center">
          <div className="bg-white/20 backdrop-blur-lg rounded-3xl p-8 text-white">
            <h2 className="text-3xl font-bold mb-4">Comece agora</h2>
            <p className="text-lg text-white/90">
              Crie sua conta e tenha acesso a todas as funcionalidades por 7 dias grátis.
              Capture suas ideias por voz ou texto.
            </p>
          </div>
          
          {/* Banner */}
          {banner && !bannerDismissed && (
            <div className="mt-6 relative">
              <button
                onClick={() => setBannerDismissed(true)}
                className="absolute -top-2 -right-2 z-10 w-6 h-6 bg-white rounded-full shadow-lg flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                title="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
              
              {banner.link ? (
                <a 
                  href={banner.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow cursor-pointer"
                >
                  {banner.image_url && (
                    <img 
                      src={banner.image_url} 
                      alt={banner.title || 'Banner'} 
                      className="w-full h-32 object-cover"
                    />
                  )}
                  {(banner.title || banner.description) && (
                    <div className="p-4 text-left">
                      {banner.title && (
                        <h3 className="font-bold text-slate-900">{banner.title}</h3>
                      )}
                      {banner.description && (
                        <p className="text-sm text-slate-600 mt-1">{banner.description}</p>
                      )}
                      <p className="text-xs text-orange-500 mt-2 font-medium">Clique para saber mais →</p>
                    </div>
                  )}
                </a>
              ) : (
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  {banner.image_url && (
                    <img 
                      src={banner.image_url} 
                      alt={banner.title || 'Banner'} 
                      className="w-full h-32 object-cover"
                    />
                  )}
                  {(banner.title || banner.description) && (
                    <div className="p-4 text-left">
                      {banner.title && (
                        <h3 className="font-bold text-slate-900">{banner.title}</h3>
                      )}
                      {banner.description && (
                        <p className="text-sm text-slate-600 mt-1">{banner.description}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
